import React, { useMemo, useRef, useState } from "react";
import {
  ShieldCheck, BadgeCheck, Lock, AlertTriangle, Clock, X, Check,
  Upload, Play, Trash2,
} from "lucide-react";
import { SectionTitle } from "../primitives";
import { api } from "@/lib/api";
import { resolveImageUrl as resolveUrl } from "@/lib/img";
import { toast } from "sonner";

const STATUS_MAP = {
  not_verified: { color: "text-stone-400",   bg: "bg-stone-800/40 border-stone-700",        icon: AlertTriangle, label: "Not verified" },
  pending:      { color: "text-amber-300",   bg: "bg-amber-500/10 border-amber-500/30",     icon: Clock,         label: "Pending review" },
  verified:     { color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/40", icon: BadgeCheck,    label: "Verified" },
  rejected:     { color: "text-rose-300",    bg: "bg-rose-500/10 border-rose-500/40",       icon: X,             label: "Rejected" },
};

function deriveStatus(profile) {
  if (!profile) return "not_verified";
  if (profile.verification_status) return profile.verification_status;
  const m = profile.verification?.manual_status;
  if (m === "approved") return "verified";
  if (m === "pending")  return "pending";
  if (m === "rejected") return "rejected";
  return "not_verified";
}

const MAX_MB = 50;

export default function Step9Verification({ profile }) {
  const status = deriveStatus(profile);
  const conf = STATUS_MAP[status];
  const Icon = conf.icon;
  const submittedAt = profile?.verification_submitted_at;
  const decidedAt = profile?.verification_decided_at;
  const rejection = profile?.verification_rejection_reason;

  const canSubmit = status === "not_verified" || status === "rejected";
  const inputRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPath, setVideoPath] = useState(null);   // returned from /uploads/verification/video
  const [uploadPct, setUploadPct] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const previewUrl = useMemo(() => videoFile ? URL.createObjectURL(videoFile) : null, [videoFile]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Video too large (max ${MAX_MB}MB)`); return; }
    setVideoFile(f);
    setVideoPath(null);
    setUploadPct(0);
  };

  const doUpload = async () => {
    if (!videoFile) return;
    setUploading(true);
    setUploadPct(0);
    const form = new FormData();
    form.append("file", videoFile);
    try {
      const { data } = await api.post("/uploads/verification/video", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
          setUploadPct(pct);
        },
      });
      setVideoPath(data.path);
      toast.success("Video uploaded");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Step9] upload err:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally { setUploading(false); }
  };

  const doSubmit = async () => {
    if (!videoPath) { toast.error("Please upload the video first"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("video_path", videoPath);
      if (profile?.id) form.append("profile_id", profile.id);
      form.append("type", "profile_verified");
      await api.post("/verification/submit", form);
      setJustSubmitted(true);
      toast.success("Verification submitted — pending review");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Step9] submit err:", e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="wizard-step9">
      <SectionTitle icon={ShieldCheck} title="Verification"
        subtitle="One short video. Verified profiles earn a luxury badge and rank higher." />

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-amber-300 flex-none mt-0.5" />
        <div className="text-sm">
          <p className="text-amber-200 font-medium">Your video is private.</p>
          <p className="text-[13px] text-stone-300 mt-1 leading-relaxed">
            Your verification video is used only by moderators and never displayed publicly — not on profiles, galleries, search results or any media surfaces.
          </p>
        </div>
      </div>

      {/* Status card */}
      <div className="rounded-xl border border-stone-800 bg-stone-950/40 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.18em] text-stone-400">Status</span>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border ${conf.bg}`} data-testid={`step9-status-${status}`}>
            <Icon className={`w-3.5 h-3.5 ${conf.color}`} />
            <span className={conf.color}>{justSubmitted ? "Pending review" : conf.label}</span>
          </span>
        </div>
        {submittedAt && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-xs uppercase tracking-[0.18em] text-stone-400">Submitted</span>
            <span className="text-stone-200">{new Date(submittedAt).toLocaleString()}</span>
          </div>
        )}
        {decidedAt && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-xs uppercase tracking-[0.18em] text-stone-400">
              {status === "verified" ? "Verified on" : "Reviewed on"}
            </span>
            <span className="text-stone-200">{new Date(decidedAt).toLocaleString()}</span>
          </div>
        )}
        {status === "rejected" && rejection && (
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-200 text-sm">
            <strong>Reason:</strong> {rejection}
          </div>
        )}
        {status === "verified" && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40">
            <BadgeCheck className="w-4 h-4 text-emerald-300" />
            <span className="text-sm text-emerald-200 font-medium">Verified profile badge active</span>
          </div>
        )}
      </div>

      {/* In-wizard video upload — only when a fresh submission is allowed. */}
      {canSubmit && !justSubmitted && (
        <div className="rounded-xl border border-stone-800 bg-stone-950/40 p-4 space-y-4" data-testid="step9-upload-panel">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-stone-400">
              {status === "rejected" ? "Resubmit your video" : "Record your video"}
            </p>
            <p className="text-[13px] text-stone-400 mt-1 leading-relaxed">
              Hold your ID next to your face, say &quot;I am joining Relaxora on {new Date().toLocaleDateString()}&quot;,
              turn your head slowly left, then right. Max {MAX_MB}MB, MP4 / MOV / WebM.
            </p>
          </div>

          {!videoFile ? (
            <>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="w-full border-2 border-dashed border-stone-700 hover:border-amber-500/60 rounded-xl py-7 flex flex-col items-center justify-center gap-2 transition"
                data-testid="step9-choose-video"
              >
                <Upload className="w-6 h-6 text-stone-500" />
                <span className="text-sm text-stone-300">Choose or record a video</span>
                <span className="text-[11px] text-stone-500">max {MAX_MB}MB · mp4 / mov / webm</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="video/*"
                capture="user"
                className="hidden"
                data-testid="step9-file-input"
                onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }}
              />
            </>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden bg-black aspect-video max-h-[360px]">
                <video src={previewUrl} controls playsInline preload="metadata" className="w-full h-full object-contain" data-testid="step9-video-preview" />
              </div>
              <div className="flex items-center justify-between text-xs text-stone-400">
                <span className="truncate max-w-[60%]">{videoFile.name} ({(videoFile.size/1024/1024).toFixed(1)} MB)</span>
                <button type="button" onClick={() => { setVideoFile(null); setVideoPath(null); }}
                  className="text-rose-300 hover:text-rose-200 inline-flex items-center gap-1"
                  data-testid="step9-clear-video">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>

              {!videoPath ? (
                <button type="button" onClick={doUpload} disabled={uploading}
                  className="w-full h-11 rounded-lg bg-amber-500 text-stone-950 font-semibold hover:bg-amber-400 disabled:opacity-50"
                  data-testid="step9-upload-btn">
                  {uploading ? `Uploading… ${uploadPct}%` : "Upload video"}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-sm text-emerald-200">
                  <Check className="w-4 h-4" /> Video uploaded (private).
                </div>
              )}

              {uploading && (
                <div className="h-1.5 bg-stone-800 rounded overflow-hidden">
                  <div className="h-full bg-amber-500 transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              )}

              <button
                type="button"
                onClick={doSubmit}
                disabled={!videoPath || submitting}
                className="w-full h-11 rounded-lg border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="step9-submit-btn"
              >
                {submitting ? "Submitting…" : (<><Play className="w-4 h-4" /> Submit for verification</>)}
              </button>
            </div>
          )}
        </div>
      )}

      {(status === "pending" || justSubmitted) && (
        <div className="p-3 rounded-lg bg-stone-800/50 border border-stone-700 text-sm text-stone-300 flex items-center gap-2" data-testid="step9-pending-note">
          <Clock className="w-4 h-4 text-amber-300" />
          Verification submitted. A moderator will review your video shortly.
        </div>
      )}
    </div>
  );
}
